import { isAxiosError } from 'axios';
import { pipe, Array as A } from 'effect';
import { z } from 'zod';

import toast from '@/components/Toast';
import { htmlMessage } from '@/components/Toast/htmlMessage';

/**
 * THE way to tell the user an API call failed. Anything that needs an error
 * toast goes through `showApiErrorToast` — do not add another helper, and do not
 * copy one of the deprecated ones in `@/lib/toasts.js`.
 *
 * The invariant: a failed request never ends in nothing. The user sees the
 * backend's own message, or a message the screen chose, or the generic one —
 * never developer text, never `[object Object]`, never raw HTML, and never
 * silence, except for a failure the app has already answered itself (see
 * `ANSWERED_BY_THE_APP`).
 *
 * The problem it solves: one job, many different bodies the backend returns it
 * in, and eight functions that each recognise a subset. The old
 * `handleErrorResponseWithToast` made the caller name the shape
 * (`path: 'data.error'`); here the shapes are a schema, so a caller only
 * supplies an optional fallback for when nothing is recognised.
 *
 * Adding a shape means adding one member to `ErrorBodySchema` and one case to
 * `errorToast.test.ts` — never a new function, and never a `path` option.
 *
 * Known limitation, worth knowing before debugging one: a body whose shape is
 * not in the union is indistinguishable here from a body with nothing to say —
 * both end in the generic toast. If a screen shows "Something went wrong" while
 * the network tab shows a real message, the shape is missing from the union.
 *
 * Deliberately narrow, which is what the name says: an API error response, shown
 * as an error toast. An `error` field on a successful 200 is a different thing —
 * call `toast.error` yourself. A 200 carrying a `warning` field is a different
 * shape, not a different colour, and putting success fields in this union would
 * make the name lie — for that, take `getApiErrorMessages` and pick the toast.
 */

// Where a message may be looked for, and nowhere else. An envelope names its own
// payload, so once one is recognised the search stops inside it: an object carrying
// both a `response` and a top-level `message` is error-shaped, and that message is
// developer text ("Request failed with status code 500"), not something to show.
const PayloadSchema = z.union([
  z.object({ response: z.object({ data: z.unknown() }) }).transform((e) => e.response.data),
  // `.refine` because `z.unknown()` leaves the key optional, so without it this member
  // matches every object and swallows an already-unwrapped body.
  z
    .object({ data: z.unknown() })
    .refine((envelope) => 'data' in envelope)
    .transform((envelope) => envelope.data),
]);

const oneMessage = (message: string) => [message];

// Field-keyed validation map: { errors: { email: 'taken', name: ['too short'] } }
const FieldErrorsSchema = z.record(z.string(), z.union([z.string(), z.array(z.string())]));

const fieldMessages = (errors: z.infer<typeof FieldErrorsSchema>) =>
  pipe(
    Object.values(errors),
    A.flatMap((value) => (Array.isArray(value) ? value : [value])),
  );

const hasText = (messages: string[]) => messages.some((message) => message.trim().length > 0);

// An error page served by Flask or a proxy; markup inside a message is a different thing.
const isHtmlPage = (text: string) => /^\s*<(!doctype|html)/i.test(text);

// First match wins, and the order puts the most specific text a body carries first.
const ErrorBodySchema = z.union([
  // The backend's most common error body is a bare JSON string.
  z
    .string()
    .refine((text) => !isHtmlPage(text))
    .transform(oneMessage),
  // Beside field messages, `message` is only a generic summary such as "Validation failed".
  z
    .object({ errors: FieldErrorsSchema })
    .transform((b) => fieldMessages(b.errors))
    .refine(hasText),
  z.object({ message: z.string().min(1) }).transform((b) => oneMessage(b.message)),
  z.object({ error: z.string().min(1) }).transform((b) => oneMessage(b.error)),
  // No `.min(1)` on the element: a blank entry must not fail the whole shape and take
  // its valid siblings with it. The shared trim/filter drops it on its own.
  z.object({ error: z.array(z.string()).min(1) }).transform((b) => b.error),
  // A failed approval decision answers with the whole document: the reason sits in its
  // approvals, while the document's own `error` is null.
  z
    .object({ approvals: z.object({ error: z.string().min(1) }) })
    .transform((b) => oneMessage(b.approvals.error)),
  z.object({ detail: z.string().min(1) }).transform((b) => oneMessage(b.detail)),
  z.object({ detail: z.array(z.object({ msg: z.string() })).min(1) }).transform((b) =>
    pipe(
      b.detail,
      A.map((d) => d.msg),
    ),
  ),
]);

const statusOf = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } })?.response?.status;

// The app answers these itself, in the response interceptors: 401 and 403 sign
// the user out, 402 shows trial expiry, 426 reloads the page. Both interceptors
// fire that side effect and then re-reject — "Always rejects — the callbacks are
// side-effects, never suppress" (`interceptors.ts`) — so the error still arrives
// here, where a toast would talk over the app's own answer: "Something went
// wrong" during the user's own sign-out.
//
// Hardcoded rather than read from `REACT_APP_*_CODE`: the env indirection buys no
// flexibility (all three .env files carry the same values, and a dozen sibling
// checks hardcode theirs anyway) while costing a silent failure — on a build with
// the variable missing, `undefined === undefined` matches and suppresses every
// toast in the app.
//
// Not 404: it is handled in exactly one saga, not globally, and its body usually
// carries a message the user needs.
//
// The list is the contract the app is meant to keep. A client that does not keep it
// is a defect there, not a reason to weaken this — see the ticket's follow-ups.
const ANSWERED_BY_THE_APP = [401, 402, 403, 426];

const isAnsweredByTheApp = (error: unknown): boolean => {
  const status = statusOf(error);
  return status !== undefined && ANSWERED_BY_THE_APP.includes(status);
};

// Which value is searched for a user-facing message — exactly one, never a list of
// places tried in turn. This is the invariant the saga handlers spelled out as
// `err && err.response`: a catch block sees everything, including a TypeError from
// our own code, and a developer-facing message must never reach a toast.
const payloadOf = (error: unknown): unknown => {
  if (isAxiosError(error)) return error.response?.data;
  // A thrown Error has a `message`, but it is developer text, not a response.
  if (error instanceof Error) return undefined;

  const enveloped = PayloadSchema.safeParse(error);
  // No envelope means the call site already unwrapped the response for us.
  return enveloped.success ? enveloped.data : error;
};

/**
 * Pulls every user-facing message out of a failed API response. Accepts an
 * AxiosError, a bare `{ response: { data } }`, or an already-unwrapped body,
 * because call sites hold all three. Returns `[]` when nothing is recognised —
 * the caller decides whether that deserves a fallback.
 */
export const getApiErrorMessages = (error: unknown): string[] => {
  const parsed = ErrorBodySchema.safeParse(payloadOf(error));
  if (!parsed.success) return [];

  // Blank is not a message — a whitespace-only string would raise an empty toast,
  // which is the silence this file exists to prevent. One filter for every shape,
  // including the ones added later.
  return pipe(
    parsed.data,
    A.map((message) => message.trim()),
    A.filter((message) => message.length > 0),
  );
};

/**
 * What a fallback has to be: both halves, both non-blank. Declared once, so the type
 * and the runtime check cannot drift apart — the type is inferred from the schema.
 *
 * Deliberately not react-intl's `MessageDescriptor`, which declares every member
 * optional — that type also admits `{}`, `{ id: undefined }` and an untyped caller's
 * mutation variables, while both halves are load-bearing here: `id` is the toast's
 * dedupe key and `defaultMessage` is what renders when the locale lacks that id.
 * Every descriptor in this repo carries both anyway (an inline `defaultMessage` must
 * equal `en.js[id]`), so requiring them costs call sites nothing and turns a silent
 * downgrade to the generic message into a compile error.
 */
const ErrorToastMessageSchema = z.object({
  id: z.string().trim().min(1),
  defaultMessage: z.string().trim().min(1),
});

export type ErrorToastMessage = z.infer<typeof ErrorToastMessageSchema>;

const FallbackSchema = z.union([z.string().trim().min(1), ErrorToastMessageSchema]);

// The app's existing generic failure message, already the fallback in documents
// sagas, deferredTask and the bank-feed summary. Exported so a caller that needs
// the same wording outside a failed response reuses it rather than restating it.
export const GENERIC_ERROR_TOAST: ErrorToastMessage = {
  id: 'app.generalUnknownError',
  defaultMessage: 'Something went wrong',
};

// The second parameter stays typed as a message rather than `unknown`, which makes
// `onError: showApiErrorToast` a compile error — deliberately. TanStack calls
// `onError(error, variables, context)`, so the bare form would take the mutation
// variables as a message; eight call sites pass the deprecated helper exactly that
// way today, and it compiles only because that helper is untyped JS
// (`@/domain/payments/queries/beneficiaries.ts:74` carries a six-line comment about
// the toast it silences). Write `onError: (err) => showApiErrorToast(err)`.
//
// Parsed rather than type-asserted, and with a check of its own rather than the one
// `@/components/Toast` renders with: that one answers "should I format this child",
// where a false negative merely renders a node, while a false positive here puts an
// object into a toast and into its dedupe key. It runs at all for the callers the
// compiler never sees — most of the remaining call sites are `.js` sagas.
const asMessage = (fallback: unknown): ErrorToastMessage | string => {
  const parsed = FallbackSchema.safeParse(fallback);
  return parsed.success ? parsed.data : GENERIC_ERROR_TOAST;
};

// react-toastify does not raise a second toast while one with the same `toastId`
// is on screen — verified in `@/components/Toast/index.test.tsx`, since that is the
// library's behaviour and not ours. Keying the id by the message is what stops a
// single failure stacking up: an AG Grid filter change fires several requests at
// once, each with its own catch, and a window-focus refetch can fail again while
// the first toast is still up.
//
// Not a route change, despite how it looks: `@/sagas/navigatorV6.js` calls
// `ToastEventHandler.closeAll()` on every navigation, and that dismisses every
// toast in the app — so nothing survives a tab switch to be duplicated.
//
// Keyed by the message, never by one shared constant: a single id for every error
// collapses unrelated features into each other, so the second failure is dropped
// instead of shown.
const showOnce = (message: ErrorToastMessage | string): void => {
  if (typeof message !== 'string') {
    toast.error(message, { toastId: message.id });
    return;
  }

  const { content, hasLink } = htmlMessage(message);

  // Something to act on must not vanish under the user, and a stray click must not
  // dismiss it. Formatting alone is not a reason to linger.
  toast.error(content, { toastId: message, ...(hasLink && { autoClose: false, closeOnClick: false }) });
};

/**
 * Shows one error toast per message the failed response carries, or the generic
 * message when it carries none — the normal case for a 5xx, whose body is usually
 * HTML or empty. Something is always shown: a failure the user cannot see is the
 * bug this replaces. The exception is a failure the app has already answered
 * itself — a sign-out, trial expiry, the 426 reload — which stays silent here.
 *
 * @param fallback override only when the screen can say something more useful
 * than "Something went wrong". That is the one thing the call site knows and this
 * file cannot.
 */
export const showApiErrorToast = (error: unknown, fallback: ErrorToastMessage | string = GENERIC_ERROR_TOAST): void => {
  if (isAnsweredByTheApp(error)) return;

  const messages = getApiErrorMessages(error);

  if (messages.length) {
    messages.forEach(showOnce);
    return;
  }

  showOnce(asMessage(fallback));
};
